import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { useDeviceProfile } from '../../../shared/hooks/useDeviceProfile';
import { PerimeterPlanStill } from './PerimeterPlanStill';
import { SceneBoundary } from './SceneBoundary';

const PerimeterCanvas = lazy(() =>
  import('./PerimeterCanvas').then((module) => ({ default: module.PerimeterCanvas })),
);

const STILL_TITLE =
  'Survey plan of a guarded stand: fence line, two structures, four camera positions and the gate.';

/*
 * Measured, not chosen. 1400ms is the value that took mobile performance from
 * 0.87 to 0.96; a later 2000ms trial dragged out the blocking-time window and
 * was reverted.
 */
const SCENE_BACKSTOP_MS = 1400;

/**
 * Decides what fills the hero plane, and says so out loud when it downgrades.
 *
 * The drawn still is the prerendered frame, the loading state and the fallback,
 * so the plane is never empty — on first paint, with motion turned down, or when
 * the scene cannot start.
 */
export function PerimeterStage() {
  const { reducedMotion, compact, webglAvailable, measured } = useDeviceProfile();
  const hostRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(true);
  const [ready, setReady] = useState(false);

  const still = <PerimeterPlanStill title={STILL_TITLE} />;
  const useStill = measured && (reducedMotion || !webglAvailable);

  useEffect(() => {
    if (!useStill) {
      return;
    }

    const reason = reducedMotion ? 'prefers-reduced-motion is set' : 'WebGL is unavailable';
    console.info(`[black-bear] perimeter canvas fell back to the drawn plan still: ${reason}.`);
  }, [reducedMotion, useStill]);

  // The scene chunk is the heaviest thing the page downloads. Fetching it during
  // the first paint competes for bandwidth with the hero itself, so it waits for
  // idle, for the reader to scroll, or for a backstop. The drawn still holds the
  // frame until it arrives — the reader never sees an empty plane.
  //
  useEffect(() => {
    if (!measured || useStill) {
      return;
    }

    let done = false;

    const begin = () => {
      if (done) {
        return;
      }

      done = true;
      cleanup();
      setReady(true);
    };

    const idle =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(begin, { timeout: SCENE_BACKSTOP_MS })
        : window.setTimeout(begin, 400);
    const backstop = window.setTimeout(begin, SCENE_BACKSTOP_MS);

    function cleanup() {
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idle as number);
      } else {
        window.clearTimeout(idle as number);
      }

      window.clearTimeout(backstop);
      window.removeEventListener('scroll', begin);
      window.removeEventListener('touchstart', begin);
    }

    window.addEventListener('scroll', begin, { passive: true, once: true });
    window.addEventListener('touchstart', begin, { passive: true, once: true });

    return cleanup;
  }, [measured, useStill]);

  // Rendering stops outright once the hero has scrolled away.
  useEffect(() => {
    const node = hostRef.current;
    if (!node || !ready) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: '10% 0px' },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [ready]);

  if (useStill || !ready) {
    return <div className="absolute inset-0">{still}</div>;
  }

  return (
    <div ref={hostRef} className="absolute inset-0">
      <SceneBoundary fallback={still}>
        <Suspense fallback={still}>
          <PerimeterCanvas compact={compact} active={inView} />
        </Suspense>
      </SceneBoundary>
    </div>
  );
}
